<template>
    <Head>
        <title>{{ pageData.props.page?.title }}</title>
    </Head>
    <div class="wrap teacher_wrap cms">
        <header>
            <h2> <i class="fa-solid fa-school"></i>{{ $t('pages.preSettingModules.title') }}</h2>
        </header>

        <div class="workspace">
            <div class="sidebar_area">
                <div class="sidebar">
                    <div class="module_wrap">
                        <div class="lst_modules">
                            <a
                                v-for="(module, index) in modules"
                                :key="index"
                                class="module"
                                :class="{ on: activeModuleType === module.type }"
                                @click="setActiveModuleType(module.type)"
                            >
                                <div class="module_info">
                                    <span>{{ moduleTypeLabel[module.type] }}</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div ref="contentRef" class="content" @scroll="onScroll">
                <ContentTitle v-for="module in modules" :key="module.type" :module="module" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick } from 'vue';
    import { moduleTypeLabel, ModuleTypeEnum } from '@/enums/ModuleTypeEnum';
    import ContentTitle from '@/app/components/teacher/ContentTitle.vue';
    import { usePage, Head } from '@inertiajs/vue3';
    import { PageProps } from '@/teacher/types';
    import { ModuleGuide } from '@/app/api/models/moduleGuide';
    import { size } from 'lodash';

    const pageData = usePage<PageProps>();

    const moduleGuides = computed(() => pageData.props.module_guides as ModuleGuide[]);

    const modules = computed(() => {
        return moduleGuides.value.map((guide) => ({
            id: 0,
            type: guide.type,
            mode: guide.mode ?? [],
            code: 'pre-setting-modules',
            title: '수업 설정',
            contents: [],
            questions: [],
            txt_type: '',
            created_at: '',
            updated_at: '',
            deleted_at: '',
            guide: guide,
            pages: [],
        }));
    });

    const activeModuleType = ref(modules.value[0]?.type);

    const contentRef = ref<HTMLElement>();

    function setActiveModuleType(moduleType: ModuleTypeEnum) {
        const moduleId = `module_${moduleType}`;

        const moduleElement = document.getElementById(moduleId);
        if (!moduleElement || !contentRef.value) return;

        const top = moduleElement.offsetTop - 24;
        contentRef.value.scrollTo({ top, behavior: 'smooth' });
    }

    const contentPositionTop = ref<
        {
            moduleType: ModuleTypeEnum;
            height: number;
            top: number;
        }[]
    >([]);

    onMounted(() => {
        // content의 자식 요소를 반복문으로 처리
        if (contentRef.value) {
            for (let i = 0; i < contentRef.value.children.length; i++) {
                const element = contentRef.value.children[i] as HTMLElement;
                contentPositionTop.value.push({
                    moduleType: Number(element.id.split('_')[1]) as ModuleTypeEnum,
                    height: element.offsetHeight,
                    top: element.offsetTop,
                });
            }
        }
    });

    async function onScroll() {
        await nextTick();
        if (!contentRef.value) return;

        const contentHeight = contentRef.value.offsetHeight;
        const scrollPosition = contentRef.value.scrollTop;

        if (scrollPosition === 0) {
            activeModuleType.value = contentPositionTop.value[0].moduleType;
        } else if (scrollPosition >= contentRef.value.scrollHeight - contentRef.value.offsetHeight) {
            activeModuleType.value = contentPositionTop.value[size(contentPositionTop.value) - 1].moduleType;
        } else {
            const temp = [...contentPositionTop.value].reverse();

            const topModuleIndex = temp.findIndex((top) => top.top - 24 <= scrollPosition);

            if (topModuleIndex === -1) return;
            else if (contentHeight > temp[topModuleIndex].height && topModuleIndex) {
                activeModuleType.value = temp[topModuleIndex - 1].moduleType;
            } else {
                activeModuleType.value = temp[topModuleIndex].moduleType;
            }
        }
    }
</script>
